import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ConfirmModalContent, ConfirmModalContextType } from './types';
import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { ConfirmSkipModalWrapper } from './style';
import { colors } from 'crono-fe-common/theme';
import IconSWarning from 'crono-fe-common/icons/Icon-S-Warning';
import { Typography } from '@mui/material';
import { Constants } from 'crono-fe-common/constants/constants';
import CronoCheckbox from 'crono-fe-common/components/CronoCheckbox';
import { Button, CloseMdIcon, IconButton } from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import { ConfirmModalContext } from './contexts';

const ConfirmModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [confirmModalContent, setConfirmModalContent] =
    useState<ConfirmModalContent | null>(null);

  //if we change the checkbox value the modal would nopt rerender
  //so I created an internal state to manage the checkbox value
  //and on checbkox change we update both the external checkbox value and the local one
  const [internalCheckboxValue, setInternalCheckboxValue] =
    useState<boolean>(true);

  const openModal = useCallback((content: ConfirmModalContent | null) => {
    setInternalCheckboxValue(content?.checkboxDefaultValue ?? true);
    setConfirmModalContent(content);
  }, []);

  const modalConfirm = () => {
    const currentContent = confirmModalContent;
    if (currentContent?.confirmFunction) {
      if (currentContent.showCheckbox) {
        currentContent.confirmFunction(internalCheckboxValue);
      } else {
        currentContent.confirmFunction();
      }
    }
    // confirmFunction may synchronously open a new modal (e.g. a nested
    // discard-changes confirmation) — only clear if nothing replaced it.
    setConfirmModalContent((prev) => (prev === currentContent ? null : prev));
  };

  const modalCancel = () => {
    const currentContent = confirmModalContent;
    currentContent?.cancelFunction?.();
    setConfirmModalContent((prev) => (prev === currentContent ? null : prev));
  };

  const modalClose = () => {
    const currentContent = confirmModalContent;
    currentContent?.closeFunction?.();
    setConfirmModalContent((prev) => (prev === currentContent ? null : prev));
  };

  const value: ConfirmModalContextType = useMemo(
    () => ({
      openModal,
    }),
    [openModal],
  );

  return (
    <ConfirmModalContext.Provider value={value}>
      {confirmModalContent && (
        <BlurBackgroundDivFixed
          style={{ zIndex: Constants.highestZIndex }}
          onClick={() => {
            if (confirmModalContent.disableClickOnBackground) return;
            if (confirmModalContent.cancelFunction)
              confirmModalContent.cancelFunction();
            setConfirmModalContent(null);
          }}
        >
          <ConfirmSkipModalWrapper
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
            }}
          >
            {confirmModalContent.showCloseCrossTopRight && (
              <IconButton
                className="absolute top-6 right-6"
                variant="text"
                icon={CloseMdIcon}
                label="Close"
                onClick={modalClose}
              />
            )}
            {confirmModalContent.type === 'danger' && (
              <IconSWarning className="icon-warning" color={colors.inactive} />
            )}
            <Typography
              fontSize={24}
              fontWeight={700}
              lineHeight={'30px'}
              className={cn(
                confirmModalContent.titleAlign === 'left' &&
                  'self-stretch text-left',
              )}
            >
              {confirmModalContent.title}
            </Typography>
            {confirmModalContent.text && (
              <div className="w-full text-sm leading-6 text-gray-700">
                {confirmModalContent.text}
              </div>
            )}

            {confirmModalContent.warning && (
              <div className="warning-container" style={{ marginTop: -16 }}>
                <div className="warning-icon">
                  <IconSWarning color={colors.white} />
                </div>
                <Typography
                  fontSize={12}
                  lineHeight={'16px'}
                  fontWeight={500}
                  color={colors.inactive}
                >
                  {confirmModalContent.warning}
                </Typography>
              </div>
            )}

            {confirmModalContent.showCheckbox && (
              <div className="checkbox-container">
                <CronoCheckbox
                  checked={internalCheckboxValue}
                  onClick={() => {
                    setInternalCheckboxValue((prev) => !prev);
                  }}
                />

                <Typography
                  fontSize={14}
                  lineHeight={'16px'}
                  fontWeight={500}
                  color={colors.grey11}
                >
                  {confirmModalContent.checkboxText}
                </Typography>
              </div>
            )}

            {confirmModalContent.type === 'info' ? (
              <div className="button-footer">
                <Button variant="outline" onClick={modalCancel}>
                  {confirmModalContent?.cancelText ?? 'No'}
                </Button>

                {confirmModalContent.confirmFunction && (
                  <Button onClick={modalConfirm}>
                    {confirmModalContent.confirmText ?? 'Yes'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="button-footer">
                {confirmModalContent.confirmFunction && (
                  <Button tone="red" onClick={modalConfirm}>
                    {confirmModalContent.confirmText ?? 'Yes'}
                  </Button>
                )}

                <Button variant="outline" onClick={modalCancel}>
                  {confirmModalContent?.cancelText ?? 'No'}
                </Button>
              </div>
            )}
          </ConfirmSkipModalWrapper>
        </BlurBackgroundDivFixed>
      )}
      {children}
    </ConfirmModalContext.Provider>
  );
};

export function useConfirmModal() {
  const context = useContext(ConfirmModalContext);
  if (context === undefined) {
    throw new Error(
      `useConfirmModal must be used within a ConfirmModalProvider`,
    );
  }

  return context;
}

export default ConfirmModalProvider;
